<template>
  <div
    class="container"
    :class="[
      `container--${type}`,
      { 'container--group--disabled': item.status != 1 && type === 'group' },
    ]"
  >
    <section
      :id="`typification-group-${index}`"
      :class="[
        type === 'submotive' ? 'card-submotive' : 'card',
        `card--${type}`,
        `card--${type}--${
          item.is_expanded && item.children?.length > 0 ? 'expanded' : 'collapsed'
        }`,
        {
          'card--group--disabled': item.status != 1 && type === 'group',
          'card__item--disabled': item.status != 1,
        },
      ]"
    >
      <div
        class="card__item"
        :class="[`card__item--${type}`, { 'card__item--disabled': item.status != 1 }]"
        @click="handleToggleExpanded(item)"
      >
        <div class="group">
          <span class="card__item__draggable">
            <MoreOutlined class="icon--more" />
            <MoreOutlined class="icon--more icon--more-right" />
          </span>

          <template v-if="type !== 'submotive'">
            <DownOutlined v-if="item.is_expanded" />
            <RightOutlined v-else />
          </template>
          <a-tooltip>
            <template #title>
              Puntaje de {{ getOrder(item) }} sobre {{ total }}
              {{ name.split(" ")[0] + "s activos" }}
            </template>
            <GoldOutlined />
          </a-tooltip>
          <span class="item__name">
            <a-tooltip v-if="item.name?.length > 25">
              <template #title> {{ item.name }} </template>
              {{ item.name.substring(0, 20) + "..." }}
            </a-tooltip>
            <div v-else>{{ item.name }}</div> </span
          ><span class="item__label">{{ name }} {{ getGroupTypeName(item.group_type) }}</span>
        </div>
        <div @click.stop class="card__item__actions">
          <a-space align="center">
            <!-- <slot name="linked-form" /> -->
            <a-tooltip v-if="item.form_id" :title="`Vinculado: ${item.form.name}`">
              <FileDoneOutlined class="linked-form-icon" />
            </a-tooltip>

            <a-switch :checked="item.status == 1" size="small" @change="handleToggleActive" />

            <a-popover
              v-model:visible="showMoreOptions"
              trigger="click"
              title=""
              overlay-class-name="typification-actions-popover"
            >
              <template #content>
                <a-space direction="vertical">
                  <slot name="edit" :hideMoreOptions="hideMoreOptions" />
                </a-space>
              </template>

              <a-button type="text" shape="circle">
                <template #icon>
                  <MoreOutlined />
                </template>
              </a-button>
            </a-popover>
          </a-space>
        </div>
      </div>
      <div v-if="item.is_expanded" class="card__content">
        <slot />
      </div>
    </section>
  </div>
</template>

<script>
import { defineComponent, ref } from "vue";
import {
  MoreOutlined,
  DownOutlined,
  RightOutlined,
  GoldOutlined,
  FileDoneOutlined,
} from "@ant-design/icons-vue";

import { FormsTypes } from "@/app/workspace/utils/enums";

const GroupType = {
  DirectContact: 1,
  IndirectContact: 2,
  NoContact: 3,
};

export default defineComponent({
  components: {
    MoreOutlined,
    DownOutlined,
    RightOutlined,
    GoldOutlined,
    FileDoneOutlined,
  },
  props: {
    item: {
      required: true,
      type: Object,
    },
    index: {
      required: true,
      type: Number,
    },
    parent: {
      required: false,
      type: Object,
      default: null,
    },
    name: {
      required: true,
      type: String,
    },
    type: {
      required: true,
      type: String,
    },
    total: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const showMoreOptions = ref(false);
    const item_prop = ref(props.item);

    const getGroupTypeName = (groupTypeId) => {
      if (groupTypeId === GroupType.DirectContact) {
        return "- Contacto directo";
      } else if (groupTypeId === GroupType.IndirectContact) {
        return "- Contacto indirecto";
      } else if (groupTypeId === GroupType.NoContact) {
        return "- No contacto";
      }

      return "";
    };

    const hideMoreOptions = () => {
      showMoreOptions.value = false;
    };

    return {
      FormsTypes,
      showMoreOptions,
      item_prop,
      getGroupTypeName,
      hideMoreOptions,
    };
  },
  computed: {
    fullItem() {
      return {
        ...this.item,
        index: this.index,
        parent: this.parent,
      };
    },
  },
  mounted() {
    if (this.item.status == 2) {
      this.$store.commit("SET_TYPIFICATION_DISABLED", true);
    }
  },
  methods: {
    // Interactivity
    async handleToggleExpanded(item) {
      try {
        await this.$store.dispatch("updateItemToTypificationTree", {
          id: item.id,
          type: item.type,
          group_type: item.group_type,
          is_expanded: !item.is_expanded,
        });
        this.$emit("updateTree");
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || `Ha ocurrido un error`;

        this.$message.error(errorMessage);
      }
    },
    handleToggleActive() {
      this.$emit("onToggleStatus");
    },
    getOrder(item) {
      if (item.type == "group") {
        return item.order;
      }
      if (item.type == "result") {
        return item.order?.split(".")[1];
      }
      if (item.type == "motive") {
        return item.order?.split(".")[2];
      }
      if (item.type == "submotive") {
        return item.order?.split(".")[3];
      }
    },
  },
});
</script>

<style lang="sass" scoped>
$item-color: $gray-4
.container
  position: relative

  &--group--disabled
    background-color: $item-color !important

  &--group
    margin-bottom: 28px
  .card
    user-select: none

    & .card, .card-submotive
      margin-left: 46px
    &--group
      // background: $geekblue-1
      background-color: $orange-1
      padding-right: 14px
      border-radius: 4px
      // box-shadow: inset 0px -2px 0px $blue-6
      box-shadow: inset 0px -2px 0px #FFD591

      &--disabled
        box-shadow: inset 0px -2px 0px $gray-6

      &--expanded
        padding-bottom: 12px

    &__item
      padding: 12px 16px
      margin-bottom: 4px
      display: flex
      justify-content: space-between
      align-items: center
      cursor: pointer
      position: relative

      &--disabled
        background-color: $item-color !important

        & > .group
          opacity: 0.4
        & > .card__item__actions
          & > *
            opacity: 0.4
      &--result
        // background-color: rgba(173, 198, 255, 0.7)
        background-color: rgba(255, 231, 186, 0.9)
      &--motive
        // background-color: #D6E4FF
        background-color: rgba(255, 231, 186, 0.6)
      &--submotive
        // background-color: rgba(214, 228, 255, 0.6)
        background-color: rgba(255, 231, 186, 0.4)
      &__draggable
        margin-right: 5px
        cursor: grab

    &__add
      text-decoration: underline
      display: block
      margin: 12px 48px

.item
  &__name
    color: $gray-9
    margin: 0 12px

  &__label
    color: $gray-7
    font-style: oblique

  &__edit
    margin-right: 12px

.icon--more
  font-size: 20px
  &-right
    margin-left: -14px

.icon--expanded
  margin-left: 8px

.group
  display: flex
  align-items: center

.anticon-gold
  margin-left: 12px

.linked-form-icon
  color: $gold-7
  margin-right: 8px
</style>

<style>
.typification-actions-popover .ant-popover-inner-content {
  padding: 4px 0 !important;
}

.typification-actions-popover .ant-popover-inner-content .ant-btn {
  width: 100% !important;
  text-align: left !important;
}
</style>
